import { IFormRules } from 'utils';
import { INamedEntity } from './general';
import { WorkerProjectStatus } from './project';
import { IBadgeField, IBadgeTemplate as IProjectBadgeTemplate, SupportedBadgeType } from './consentForm';
import { ICertificationGroup, IWorkerCertification } from './certification';
import { ITrainingGroup, IWorkerTraining } from './training';
import { IFcaLearningGroup, IWorkerCourse } from './fcaLearning';

export enum BadgeStatus {
  ACTIVE = 0,
  REVOKED = 1,
  DEACTIVATE = 2,
  EXPIRED = 3,
  NOT_ISSUED = 4,
  UNREVOKED = 5,
}

export enum BadgeLayout {
  LARGE_HEADER = 1,
  SMALL_HEADER = 2,
  WHITE_HEADER = 3,
  VISITOR = 4,
}

export enum BadgeType {
  UNKNOWN = -1,
  REGULAR = 0, // Physical and E-Badge
  UNIVERSAL = 1, // Physical or E-Badge
  ELECTRONIC = 2, // E-Badge Only
  PHYSICAL_OR_ELECTRONIC = 3, // Physical or E-Badge
}

export enum VisitorAvailability {
  PENDING = 0,
  AVAILABLE = 1,
  ASSIGNED = 2,
}

export enum VisitorType {
  REGULAR = 0,
  VIP = 1,
}

export enum VisitorEntity {
  CLIENT = 0,
  OTHER = 1,
}

export enum VisitorBadgeStatus {
  AVAILABLE = 0,
  ASSIGNED = 1,
}

export interface IBadgeTemplate {
  id: string;
  layout: BadgeLayout;
  color: string;
  fontColor: string;
  font: string;
  logoFileName?: string;
  logoUrl?: string;
  templateFileUrl?: string;
  templateFileName?: string;
}

export interface IBadge {
  id: string;
  badgeTemplate: IBadgeTemplate;
  code: string;
  status: BadgeStatus;
  firstName: string;
  lastName: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  pictureUrl: string;
  companyName: string;
  hardHatNumber: string;
  tagId?: any;
  expirationDate?: string;
  badgeType?: BadgeType;
  consentFormSigned?: boolean;
  trainingsCompleted?: boolean;
  certificationsCompleted?: boolean;
  workerProjectStatus?: WorkerProjectStatus;
  isSynchronizing?: boolean;
  billingAddressId?: boolean;
  projectBadgeTemplateId?: string;
  projectBadgeTemplates?: IProjectBadgeTemplate[];
  badgeFieldValues?: IBadgeField[];
  lastUpdatedBy?: string;
  lastStatusChangeBy?: string;
  statusChangeReason?: string;
  workerProjectBadgeTemplate?: { id: string };
  allExemptedCertifications?: string[];
  allExemptedTrainings?: string[];
  isControlledByLocationManager?: boolean;
  electronicBadgeCode?: string;
  workerId?: string;
  learningsCompleted?: boolean;
  upcomingTrainingsCompleted?: boolean;
  upcomingCertificationsCompleted?: boolean;
  upcomingLearningsCompleted?: boolean;
  printCount?: number;
  mustAutoDeactivate?: boolean;
  firstActivatedAt?: string;
  projectAcsBadgeSyncStatuses?: {
    deviceName: string;
    location: string;
    lastKnownSyncError: string;
    isSynchronizing: boolean;
  }[];
  canShowExpirationDate?: boolean;
  canShowExpirationInDays?: boolean;
  isExpirationDateVisibleOnBadge?: boolean;
  expiresInDays?: number;
  expirationDisabled?: boolean;
}

export enum BadgeAction {
  UNKNOWN = 0,
  ACTIVATE = 1,
  DEACTIVATE = 2,
  REVOKE = 3,
  REACTIVATE = 4,
  ADD_TO_ACS = 5,
  REMOVE_FROM_ACS = 6,
  UPDATE_EXPIRATION_DATE = 7,
  GENERATE_PDF = 8,
  INSERT_TAG_ID = 9,
  NO_ACTION = 10,
}

export interface IBadgeHistory {
  id: string;
  date: string;
  badgeAction: BadgeAction;
  tagId: any;
  description: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  projectAccessControlSystem: {
    id: string;
    deviceName: string;
  };
  reader: {
    id: string;
    hostname: string;
  };
  isAssociatedToBillingEvent?: boolean;
}

export interface IBadgeUpdateRequest {
  billingAddressId?: string;
  expirationDate?: string;
  hasExpiration?: number;
  tagId?: any;
  projectBadgeTemplateId?: string;
  badgeFieldValues?: IBadgeField[];
  statusChangeReason?: string;
  addSpecialInstructions?: boolean;
  specialInstructions?: string;
  isExpirationDateVisibleOnBadge?: boolean;
  expiresInDays?: number;
  expirationDisabled?: boolean;
}

export interface IBadgeVisitor {
  id: string;
  number: number;
  code: string;
  status: BadgeStatus;
  availability: VisitorAvailability;
  firstName: string;
  lastName: string;
  visitorType: VisitorType;
  validFrom: string;
  validTo: string;
  description: string;
  company?: INamedEntity;
  companyId?: string;
  companyLabel?: string;
  entityName: string;
  visitorBadgeStatus: VisitorBadgeStatus;
  tagId?: any;
  isSynchronizing?: boolean;
  projectAcsBadgeSyncStatuses?: {
    deviceName: string;
    location: string;
  }[];
}

export const badgeActionMap = {
  [BadgeAction.UNKNOWN]: () => 'Unknown',
  [BadgeAction.ACTIVATE]: () => 'Activate the badge',
  [BadgeAction.DEACTIVATE]: () => 'Deactivate the badge',
  [BadgeAction.REVOKE]: () => 'Revoke the badge',
  [BadgeAction.REACTIVATE]: () => 'Reactivate the badge',
  [BadgeAction.ADD_TO_ACS]: (name: string) => `Badge added on ACS ${name}`,
  [BadgeAction.REMOVE_FROM_ACS]: (name: string) => `Badge removed on ACS ${name}`,
  [BadgeAction.UPDATE_EXPIRATION_DATE]: () => 'Change the expiration date',
  [BadgeAction.GENERATE_PDF]: () => 'Generate PDF',
  [BadgeAction.INSERT_TAG_ID]: () => 'Insert Tag ID',
};

export const badgeOptionList = [{ title: 'Deactivate' }, { title: 'Revoke' }, { title: 'Reprint' }];

export const badgeStatusMap = {
  [BadgeStatus.ACTIVE]: 'Active',
  [BadgeStatus.REVOKED]: 'Revoked',
  [BadgeStatus.DEACTIVATE]: 'Deactivated',
  [BadgeStatus.EXPIRED]: 'Expired',
  [BadgeStatus.NOT_ISSUED]: 'Not Issued',
};

export const modalStatusMap = {
  [BadgeStatus.ACTIVE]: () => ({
    title: 'Activate Badge?',
    text: `Enter Badge Tag ID`,
    confirmLabel: 'Activate',
    loadingText: 'Activating...',
  }),
  [BadgeStatus.DEACTIVATE]: () => ({
    title: 'Deactivate Badge?',
    text: 'Please enter the reason to deactivate the Badge.',
    confirmLabel: 'Deactivate',
    loadingText: 'Deactivating...',
  }),
  [BadgeStatus.REVOKED]: () => ({
    title: 'Revoke Badge?',
    text: 'Please enter the reason to revoke the Badge.',
    confirmLabel: 'Revoke',
    loadingText: 'Revoking...',
  }),
  [BadgeStatus.EXPIRED]: () => ({}),
  [BadgeStatus.UNREVOKED]: () => ({
    title: 'Unrevoke Badge?',
    text: `Enter Badge Tag ID`,
    confirmLabel: 'Unrevoke',
    loadingText: 'Unrevoking...',
  }),
};

export const visitorAvailabilityMap = {
  [VisitorAvailability.AVAILABLE]: 'Available',
  [VisitorAvailability.ASSIGNED]: 'Assigned',
};

export const visitorTypeMap = {
  [VisitorType.REGULAR]: 'Regular',
  [VisitorType.VIP]: 'VIP',
};

export const visitorEntityMap = {
  [VisitorEntity.CLIENT]: 'Company',
  [VisitorEntity.OTHER]: 'Other',
};

export const entityOptionList = [
  { value: VisitorEntity.CLIENT, label: visitorEntityMap[VisitorEntity.CLIENT] },
  { value: VisitorEntity.OTHER, label: visitorEntityMap[VisitorEntity.OTHER] },
];

export enum BadgeModalAction {
  ACTIVE = 0,
  NOT_ACTIVE = 1,
}

export enum BadgePrintAddressType {
  BillingSite = 0,
  JobSite = 1,
  GeneralContractor = 2,
  Developer = 3,
  ProjectOwner = 4,
}

export const getFallbackBadgeTemplate = (): IBadgeTemplate => ({
  id: null,
  layout: BadgeLayout.LARGE_HEADER,
  color: '1E86E8',
  fontColor: '444444',
  font: 'Arial',
  logoUrl: null,
  templateFileUrl: null,
  templateFileName: null,
});

export const getFallbackVisitorBadgeTemplate = (): IBadgeTemplate => ({
  id: null,
  layout: BadgeLayout.VISITOR,
  color: 'FFE304',
  fontColor: '444444',
  font: 'Arial',
  logoUrl: null,
  templateFileUrl: null,
  templateFileName: null,
});

export const getFallbackBadge = (isVisitor = false): IBadge => ({
  id: null,
  badgeTemplate: isVisitor ? getFallbackVisitorBadgeTemplate() : getFallbackBadgeTemplate(),
  status: BadgeStatus.DEACTIVATE,
  firstName: null,
  lastName: null,
  emergencyContactName: null,
  emergencyContactPhone: null,
  emergencyContactRelationship: null,
  companyName: null,
  hardHatNumber: null,
  code: null,
  pictureUrl: null,
  tagId: null,
  expirationDate: null,
  consentFormSigned: false,
  trainingsCompleted: false,
  certificationsCompleted: false,
  badgeType: BadgeType.REGULAR,
  lastStatusChangeBy: null,
  workerProjectStatus: WorkerProjectStatus.PENDING,
  isControlledByLocationManager: false,
});

export const getFallbackBadgeVisitor = (): IBadgeVisitor => ({
  id: null,
  number: null,
  code: null,
  firstName: null,
  lastName: null,
  validFrom: null,
  validTo: null,
  companyId: null,
  entityName: null,
  description: null,
  visitorType: VisitorType.REGULAR,
  status: BadgeStatus.DEACTIVATE,
  availability: VisitorAvailability.AVAILABLE,
  visitorBadgeStatus: VisitorBadgeStatus.AVAILABLE,
  tagId: null,
});

export enum BadgeTemplateType {
  Owner = 0,
  GeneralContractor = 1,
  SubContractor = 2,
  Visitor = 3,
  Other = 4,
}

export const badgeTemplateTypeMap = {
  [BadgeTemplateType.Owner]: 'Owner',
  [BadgeTemplateType.GeneralContractor]: 'General Contractor',
  [BadgeTemplateType.SubContractor]: 'Subcontractor',
  [BadgeTemplateType.Visitor]: 'Visitor',
  [BadgeTemplateType.Other]: 'Other',
};

export const badgeTemplateTypeOptionList = [
  { value: BadgeTemplateType.Owner, label: badgeTemplateTypeMap[BadgeTemplateType.Owner] },
  { value: BadgeTemplateType.GeneralContractor, label: badgeTemplateTypeMap[BadgeTemplateType.GeneralContractor] },
  { value: BadgeTemplateType.SubContractor, label: badgeTemplateTypeMap[BadgeTemplateType.SubContractor] },
  { value: BadgeTemplateType.Visitor, label: badgeTemplateTypeMap[BadgeTemplateType.Visitor] },
  { value: BadgeTemplateType.Other, label: badgeTemplateTypeMap[BadgeTemplateType.Other] },
];

export enum BadgeTemplateStatus {
  Active = 0,
  InActive = 1,
}

export const badgeTemplateStatusMap = {
  [BadgeTemplateStatus.Active]: 'Active',
  [BadgeTemplateStatus.InActive]: 'In active',
};

export interface IBadgeNewTemplate {
  id?: string;
  name: string;
  type: number;
  status: number;
  isDefault: boolean;
  description: string;
  supportedBadgeType?: any;
  templateFileName: string;
  replacedByBadgeId?: string;
  exemptedCertifications?: string[];
  deactivateWorkerBadgeAfterDays?: number;
  isControlledByLocationManager?: boolean;
  isExemptedFromAcsSchedule?: boolean;
  addSpecialInstructions?: boolean;
  specialInstructions?: string;
  expiresInDays?: number;
  expiresOn?: string;
  applyChangesToAllBadges?: boolean;
}

export const getNewFallbackBadgeTemplate = (): IBadgeNewTemplate => ({
  name: null,
  type: null,
  isDefault: false,
  description: null,
  templateFileName: null,
  supportedBadgeType: null,
  exemptedCertifications: [],
  status: BadgeTemplateStatus.Active,
  deactivateWorkerBadgeAfterDays: null,
  isControlledByLocationManager: false,
  isExemptedFromAcsSchedule: false,
  addSpecialInstructions: false,
  specialInstructions: '',
  expiresOn: null,
  expiresInDays: null,
  applyChangesToAllBadges: false,
});

export const badgeTemplateRules = (): IFormRules => ({
  name: {
    required: true,
  },
  type: {
    required: true,
  },
  supportedBadgeType: {
    required: true,
  },
  description: {
    required: false,
  },
  templateFileName: {
    required: true,
  },
  exemptedCertifications: {
    required: false,
  },
  deactivateWorkerBadgeAfterDays: {
    required: false,
  },
  isControlledByLocationManager: {
    required: false,
  },
  expiresInDays: {
    required: false,
  },
  expiresOn: {
    required: false,
  },
  specialInstructions: {
    required: false,
  },
});

export interface ICustomField {
  id?: string;
  miscellaneous: string;
  order?: boolean;
}

export const getFallbackCustomFields = (): ICustomField => ({
  miscellaneous: null,
});

export interface IBadgeType {
  id: string | SupportedBadgeType;
  name: string;
  isDefault: boolean;
}

export interface ILocation {
  locationId: string;
  locationName: string;
  locationCode: string;
  status: string;
  isLocationManagerEnabled: boolean;
  lastActivityAt: string;
}

export interface IWorkerBadgeRequirements {
  workerCertifications: IWorkerCertification[];
  workerTrainings: IWorkerTraining[];
  workerCourses: IWorkerCourse[];
  allExemptedCertifications: string[];
  allExemptedCourses: string[];
  allExemptedTrainings: string[];
  certificationGroups: ICertificationGroup[];
  trainingGroups: ITrainingGroup[];
  learningGroups: IFcaLearningGroup[];
}
